import clsx from 'clsx';
import React, { useState } from 'react';
import Spacer from '../../../components/presentational/Spacer';
import styled, { StylesProps, cx } from '../../../lib/styles';
import BlockWrapper from '../BlockWrapper';
import { CallToActionButton } from '../CallToActionButton';
import {
  ButtonPosition,
  DefaultBlockFields,
  PositionWithinBlock,
} from '../GroupBlock';

import contentLayoutBlockStyles from './ContentLayoutBlock_styles';
import Typography from '../../../components/presentational/Typography';
import { Brand } from '../../../lib/theme';
import BlockTitle from '../BlockTitle';
import _ from 'lodash';

export interface ContentLayoutBlockProps {
  type: 'acf_contentlayout_block';
  brand: string;
  classes: string;
  background_colour: string;
  desktop_spacing: string;
  mobile_spacing: string;
  block: ContentLayoutBlockFields;
}

interface ColumnContent {
  heading: string;
  image: string;
  image_position: PositionWithinBlock;
  content: string;
}

export interface ContentLayoutBlockFields extends DefaultBlockFields {
  sidebar_image: string;
  mobile_sidebar_image: string;
  left_hand_sidebar: boolean;
  sidebar_width: number;
  block_heading: string;
  columns: ColumnContent[];
  block_footer: string;
}

const ContentLayoutBlock = ({
  type,
  classes,
  brand,
  background_colour,
  desktop_spacing,
  mobile_spacing,
  block: {
    title,
    subtitle,
    sidebar_image,
    mobile_sidebar_image,
    left_hand_sidebar,
    sidebar_width,
    block_heading,
    columns,
    block_footer,
    cta_button,
  },
  styles,
}: ContentLayoutBlockProps & StylesProps<typeof contentLayoutBlockStyles>) => {
  // console.log('ContentLayoutBlock', {
  //   sidebar_image,
  //   mobile_sidebar_image,
  //   left_hand_sidebar,
  //   block_heading,
  //   columns,
  // });
  const total_columns = columns.length;
  return (
    <BlockWrapper
      {...{
        type,
        classes,
        brand,
        background_colour,
        desktop_spacing,
        mobile_spacing,
      }}
    >
      <Spacer
        space={['m', 'l']}
        className={cx(`contentlayout-container container`, styles.container)}
      >
        {(cta_button.label || cta_button.icon) &&
          (cta_button.button_position == ButtonPosition.Above ||
            cta_button.button_position == ButtonPosition.AboveAndBelow) && (
            <CallToActionButton
              {..._.merge(cta_button, { cta_classes: 'mb-2' })}
            />
          )}

        <BlockTitle {...{ title, subtitle, brand }} />

        <div className="d-flex" style={{ gap: '1rem' }}>
          {(sidebar_image || mobile_sidebar_image) && (
            <div
              className={`${left_hand_sidebar ? 'order-0' : 'order-1'}`}
              style={{
                flex: `0 0 ${sidebar_width}%`,
                width: `${sidebar_width}%`,
                maxWidth: `${sidebar_width}%`,
              }}
            >
              {sidebar_image && (
                <img
                  loading="lazy"
                  src={sidebar_image}
                  className={`img-fluid ${styles.desktopImage}`}
                />
              )}
              {mobile_sidebar_image && (
                <img
                  loading="lazy"
                  src={mobile_sidebar_image}
                  className={`img-fluid ${styles.mobileImage}`}
                />
              )}
            </div>
          )}
          <div className="d-flex flex-column justify-content-center flex-grow-1">
            {block_heading && (
              <h3 className={styles.block_heading}>{block_heading}</h3>
            )}
            <div
              className={clsx(`d-flex justify-content-between mb-2`, {
                'justify-content-center': total_columns == 1,
                'justify-content-between': total_columns > 1,
              })}
              style={{
                flexWrap: 'nowrap',
                gap: '1rem',
              }}
            >
              {columns.map((column, i) => (
                <div
                  key={i}
                  className={clsx(`d-flex`, {
                    'flex-column':
                      column.image_position === PositionWithinBlock.Above ||
                      column.image_position === PositionWithinBlock.Below,
                  })}
                  style={{
                    gap: '1rem',
                    flexBasis:
                      total_columns > 1
                        ? `calc(${100 / total_columns}% - 1rem)`
                        : `auto`,
                    maxWidth:
                      total_columns > 1
                        ? `calc(${100 / total_columns}% - 1rem)`
                        : `auto`,
                  }}
                >
                  {column.image && (
                    <div
                      className={clsx(
                        `column-image-container d-flex align-items-center justify-content-center`,
                        {
                          'order-1':
                            column.image_position ===
                              PositionWithinBlock.Below ||
                            column.image_position === PositionWithinBlock.Right,
                        }
                      )}
                      style={{
                        maxWidth:
                          column.image_position === PositionWithinBlock.Below ||
                          column.image_position === PositionWithinBlock.Above
                            ? '100%'
                            : '20%',
                      }}
                    >
                      <img
                        loading="lazy"
                        src={column.image}
                        alt={column.heading}
                        style={{
                          maxWidth: '100%',
                        }}
                      />
                    </div>
                  )}
                  {column.heading && column.content && (
                    <div
                      className={
                        'd-flex flex-column flex-grow-1 justify-content-center'
                      }
                    >
                      {column.heading && (
                        <h3 className={styles.columnHeading}>
                          {column.heading}
                        </h3>
                      )}
                      {column.content && (
                        <p className={styles.columnContent}>{column.content}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        {block_footer && (
          <div dangerouslySetInnerHTML={{ __html: block_footer }} />
        )}
        {(cta_button.label || cta_button.icon) &&
          (cta_button.button_position == ButtonPosition.Below ||
            cta_button.button_position == ButtonPosition.AboveAndBelow) && (
            <CallToActionButton
              {..._.merge(cta_button, { cta_classes: 'mt-4 mb-2' })}
            />
          )}
      </Spacer>
    </BlockWrapper>
  );
};

export default styled(contentLayoutBlockStyles)(ContentLayoutBlock);
